if (!customElements.get('popout-select')) {
  customElements.define(
    'popout-select',
    class Popout extends HTMLElement {
      constructor() {
        super();

        this.updateOptionsEvent = (evt) => this.updateOptions(evt);
      }

      connectedCallback() {
        // Prevent duplicate initialization
        if (this.hasAttribute('data-popout-initialized')) return;
        this.setAttribute('data-popout-initialized', 'true');

        this.popoutList = this.querySelector('[data-popout-list]');
        this.popoutToggle = this.querySelector('[data-popout-toggle]');
        this.popoutToggleText = this.querySelector('[data-popout-toggle-text]');
        this.popoutInput = this.querySelector('[data-popout-input]') || this.parentNode.querySelector('[data-popout-input]') || this.parentNode.parentNode.querySelector('[data-quantity-input]');

        this.popoutOptions = this.querySelectorAll('[data-popout-option]');
        this.fireSubmitEvent = this.hasAttribute('submit');

        this.popupToggleFocusoutEvent = (evt) => this.onPopupToggleFocusout(evt);
        this.popupListFocusoutEvent = (evt) => this.onPopupListFocusout(evt);
        this.popupToggleClickEvent = (evt) => this.onPopupToggleClick(evt);
        this.keyUpEvent = (evt) => this.onKeyUp(evt);
        this.bodyClickEvent = (evt) => this.onBodyClick(evt);

        this._connectOptions();
        this._connectToggle();
        this._onFocusOut();
        this.popupListSetDimensions();

        this.addEventListener('theme:popout:update', this.updateOptionsEvent);
      }

      disconnectedCallback() {
        this.removeEventListener('theme:popout:update', this.updateOptionsEvent);
      }

      onPopupToggleClick(evt) {
        const button = evt.currentTarget;
        const ariaExpanded = button.getAttribute('aria-expanded') === 'true';

        evt.currentTarget.setAttribute('aria-expanded', !ariaExpanded);
        this.popoutList.classList.toggle('popout-list--visible');
        this.popupListSetDimensions();
        this.toggleListPosition();

        document.body.addEventListener('click', this.bodyClickEvent);
      }

      onPopupToggleFocusout(evt) {
        if (!document.body.classList.contains('is-focused')) return;

        const popoutLostFocus = this.contains(evt.relatedTarget);

        if (!popoutLostFocus) {
          this._hideList();
        }
      }

      onPopupListFocusout(evt) {
        if (!document.body.classList.contains('is-focused')) return;

        const childInFocus = evt.currentTarget.contains(evt.relatedTarget);
        const isVisible = this.popoutList.classList.contains('popout-list--visible');

        if (isVisible && !childInFocus) {
          this._hideList();
        }
      }

      hasOverflowParent() {
        let element = this.parentElement;
        while (element && element !== document.body) {
          const style = getComputedStyle(element);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            return true;
          }
          element = element.parentElement;
        }
        return false;
      }

      toggleListPosition() {
        const button = this.querySelector('[data-popout-toggle]');
        const popoutTop = this.getBoundingClientRect().top + this.clientHeight;
        const hasOverflowParent = this.hasOverflowParent();

        const removeTopClass = () => {
          if (button.getAttribute('aria-expanded') !== 'true') {
            // Don't remove the class if inside a container with overflow auto/scroll
            // Even though `select-popout__list` is absolute, it extends the scrollable container and creates a scrollbar
            if (!hasOverflowParent) {
              this.popoutList.classList.remove('popout-list--top');
            }
          }

          this.popoutList.removeEventListener('transitionend', removeTopClass);
        };

        if (button.getAttribute('aria-expanded') === 'true') {
          this.popoutList.classList.toggle('popout-list--top', theme.windowHeight / 2 < popoutTop);
        } else {
          this.popoutList.addEventListener('transitionend', removeTopClass);
        }
      }

      calculateConfinedDimensions() {
        const confineContainer = this.closest('[data-popout-select-confine]');
        if (!confineContainer) return null;

        const toggleRect = this.popoutToggle.getBoundingClientRect();
        const containerRect = confineContainer.getBoundingClientRect();
        const containerClientHeight = confineContainer.clientHeight;
        const containerScrollTop = confineContainer.scrollTop;

        // Get toggle position relative to container's content area
        const toggleRelativeTop = toggleRect.top - containerRect.top + containerScrollTop;
        const toggleRelativeBottom = toggleRect.bottom - containerRect.top + containerScrollTop;

        // Calculate viewport available space
        const viewportSpaceAbove = toggleRect.top;
        const viewportSpaceBelow = theme.windowHeight - toggleRect.bottom;

        // Calculate confined container available space
        const containerSpaceAbove = toggleRelativeTop;
        const containerSpaceBelow = containerClientHeight - toggleRelativeBottom;

        // Use the smaller space for each direction
        const effectiveSpaceAbove = Math.min(viewportSpaceAbove, containerSpaceAbove);
        const effectiveSpaceBelow = Math.min(viewportSpaceBelow, containerSpaceBelow);

        // Determine if opening on top based on effective available space
        const shouldOpenTop = effectiveSpaceAbove > effectiveSpaceBelow && effectiveSpaceAbove > 0;

        // Set the class based on the calculation
        this.popoutList.classList.toggle('popout-list--top', shouldOpenTop);

        // Calculate max-height based on opening direction using the smaller space
        const maxHeight = shouldOpenTop ? Math.max(0, parseInt(effectiveSpaceAbove)) : Math.max(0, parseInt(effectiveSpaceBelow));

        return {
          width: null,
          height: maxHeight,
        };
      }

      popupListSetDimensions() {
        this.popoutList.style.setProperty('--max-width', '100vw');
        this.popoutList.style.setProperty('--max-height', '100vh');

        requestAnimationFrame(() => {
          const popoutListRect = this.popoutList.getBoundingClientRect();
          const toggleRect = this.popoutToggle.getBoundingClientRect();

          // Viewport-based calculations
          let maxWidth = parseInt(theme.windowWidth - popoutListRect.left);
          let maxHeight;

          const isOpeningTop = this.popoutList.classList.contains('popout-list--top');
          if (isOpeningTop) {
            // When opening on top: space from viewport top to toggle button top
            maxHeight = parseInt(toggleRect.top);
          } else {
            // When opening on bottom: space from popout list top to viewport bottom
            maxHeight = parseInt(theme.windowHeight - popoutListRect.top);
          }

          // Override with confined container calculations if available
          const {width: confinedWidth, height: confinedHeight} = this.calculateConfinedDimensions() || {};
          maxWidth = confinedWidth ?? maxWidth;
          maxHeight = confinedHeight ?? maxHeight;

          this.popoutList.style.setProperty('--max-width', `${maxWidth}px`);
          this.popoutList.style.setProperty('--max-height', `${maxHeight}px`);
        });
      }

      popupOptionsClick(evt) {
        const link = evt.target.closest('[data-popout-option]');

        if (link && link.attributes.href.value === '#') {
          evt.preventDefault();

          const attrValue = link.hasAttribute('data-value') ? link.getAttribute('data-value') : '';

          this.popoutInput.value = attrValue;

          // Sync option metadata onto the hidden input so downstream logic can read it
          const listItem = link.closest('li');
          if (listItem) {
            const optionValueId = listItem.getAttribute('data-option-value-id');
            const productUrl = listItem.getAttribute('data-product-url');
            const variantId = link.getAttribute('data-variant-id');
            if (optionValueId) this.popoutInput.setAttribute('data-option-value-id', optionValueId);
            if (productUrl) this.popoutInput.setAttribute('data-product-url', productUrl);
            // set the variant ID on the hidden input so it can be used to trigger a variant change in 'variant-selects' elements' methods
            if (variantId) this.popoutInput.setAttribute('data-variant-id', variantId);
          }

          if (this.popoutInput.disabled) {
            this.popoutInput.removeAttribute('disabled');
          }

          if (this.fireSubmitEvent) {
            this._submitForm(attrValue);
          } else {
            const currentTarget = evt.currentTarget;

            // Fire a bubbling change event so parent controllers can react
            this.popoutInput.dispatchEvent(new Event('change', {bubbles: true}));

            if (this.popoutInput.name == 'quantity' && !currentTarget.parentElement.nextSibling) {
              this.classList.add('is-hidden');
            }

            this.changeStates(currentTarget);
            this.updateButtonText(attrValue);

            // Close the dropdown after selection
            this._hideList();
          }
        }
      }

      changeStates(target) {
        if (!target) return;

        const parent = target.parentElement;
        const listTargetElement = this.popoutList.querySelector('.is-active');
        const targetAttribute = this.popoutList.querySelector('[aria-current]');

        if (listTargetElement) {
          listTargetElement.classList.remove('is-active');
          parent.classList.add('is-active');
        }

        if (targetAttribute && targetAttribute.hasAttribute('aria-current')) {
          targetAttribute.removeAttribute('aria-current');
          target.setAttribute('aria-current', 'true');
        }
      }

      updateButtonText(text) {
        if (text !== '') {
          this.popoutToggleText.innerHTML = text;

          if (this.popoutToggleText.hasAttribute('data-popout-toggle-text') && this.popoutToggleText.getAttribute('data-popout-toggle-text') !== '') {
            this.popoutToggleText.setAttribute('data-popout-toggle-text', text);
          }
        }
      }

      updateOptions(evt) {
        if (evt && evt.detail && evt.detail.value) {
          const value = evt.detail.value;
          const target = this.querySelector(`[data-popout-option][data-value="${value}"]`);
          this.changeStates(target);
          this.updateButtonText(value);
        }
      }

      onKeyUp(evt) {
        if (evt.code !== 'Escape') {
          return;
        }
        this._hideList();
        this.popoutToggle.focus();
      }

      onBodyClick(evt) {
        const isOption = this.contains(evt.target);
        const isVisible = this.popoutList.classList.contains('popout-list--visible');

        if (isVisible && !isOption) {
          this._hideList();
        }
      }

      _connectToggle() {
        this.popoutToggle.addEventListener('click', this.popupToggleClickEvent);
      }

      _connectOptions() {
        if (this.popoutOptions.length) {
          this.popoutOptions.forEach((element) => {
            element.addEventListener('click', (evt) => this.popupOptionsClick(evt));
          });
        }
      }

      _onFocusOut() {
        this.addEventListener('keyup', this.keyUpEvent);
        this.popoutToggle.addEventListener('focusout', this.popupToggleFocusoutEvent);
        this.popoutList.addEventListener('focusout', this.popupListFocusoutEvent);
      }

      _submitForm() {
        const form = this.closest('form');
        if (form) {
          form.submit();
        }
      }

      _hideList() {
        this.popoutList.classList.remove('popout-list--visible');
        this.popoutToggle.setAttribute('aria-expanded', false);
        this.toggleListPosition();
        document.body.removeEventListener('click', this.bodyClickEvent);
      }
    }
  );
}
